import { BIOCARE_PORTAL_MERCY_URL, BIOCARE_PORTAL_URL } from '@/shared/config';
import { auth } from '@/shared/lib';
import { CustomButton } from '@/shared/ui/components';
import { Modal } from 'antd';
import dayjs from 'dayjs';
import { t } from 'ttag';
import type { VerifyEmailModalProps } from './verifyEmailModal.types';
import './verifyEmailModal.scss';

/**
 * Blocking modal that asks the signed-in user to verify their email address before a stated deadline. Compares today's end of day against the deadline with dayjs to choose between the mandatory-verification copy and a dated reminder, hides the Later button once the deadline has passed, and sends the browser to the Biocare verify-email page on confirm.
 *
 * @param open - Whether the modal is visible.
 * @param onCancel - Called when the user dismisses the modal with the Later button.
 * @param verifyEmailDeadline - Date by which the email must be verified, used for the deadline comparison and the displayed date.
 * @returns The verify email modal element.
 */
const VerifyEmailModal = ({ open, onCancel, verifyEmailDeadline }: VerifyEmailModalProps) => {
  /**
   * Sends the browser to the Biocare portal verify-email page. Reads loginFrom from localStorage to pick the Mercy or default portal base URL, and prefixes a slash separator only when the hostname is localhost.
   *
   * @returns Nothing.
   */
  const onClickVerifyEmail = () => {
    const seperator = window.location.hostname === 'localhost' ? '/' : '';
    const loginFrom = auth.getLoginFrom();
    const portalUrl = loginFrom === 'Mercy' ? BIOCARE_PORTAL_MERCY_URL : BIOCARE_PORTAL_URL;
    window.location.href = `${portalUrl}${seperator}verify-email`;
  };

  const isDeadlinePassed = dayjs().endOf('d').isAfter(dayjs(verifyEmailDeadline).endOf('d'));

  return (
    <Modal
      maskClosable={false}
      closable={false}
      open={open}
      onCancel={onCancel}
      className="verify-email-modal"
      footer={
        <div className="verify-email-modal__footer">
          {!isDeadlinePassed && (
            <CustomButton style="neutral" onClick={onCancel}>
              {t`Later`}
            </CustomButton>
          )}
          <CustomButton onClick={onClickVerifyEmail}>{t`Verify Email`}</CustomButton>
        </div>
      }
      centered
      title={t`Email verification required!`}
      classNames={{ header: 'verify-email-modal__header' }}
    >
      <div className="verify-email-modal__content">
        {isDeadlinePassed ? (
          <span>
            {t`To continue using our service without further interruptions, please verify your email address immediately. This is mandatory for account security.`}
          </span>
        ) : (
          <div>
            <span>
              Please complete verification by{' '}
              <strong>{dayjs(verifyEmailDeadline).format('MM/DD/YYYY')}</strong> for optimal
              functionality of your portal.
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default VerifyEmailModal;
